import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ShieldCheck,
    Search,
    Trash2,
    Loader2,
    MapPin,
    User as UserIcon,
    Users,
    UserPlus,
    Package,
    Repeat,
    Mail,
    KeyRound,
    Copy,
    Check as CheckIcon,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    fetchAdminMe,
    fetchAdminStats,
    fetchAdminUsers,
    adminSearchItems,
    adminDeleteItem,
    adminGenerateResetLink,
    adminResetMarketplace,
} from "../lib/api";

const STATUS_CLASS = {
    active: "bg-[#737a3d]/15 text-[#737a3d]",
    rented: "bg-[#B56C5A]/15 text-[#B56C5A]",
    inactive: "bg-[#2e3220]/10 text-[#2e3220]/80",
};

const formatJoinDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return "—";
    }
};

const UsersList = ({ users, filter }) => {
    const q = (filter || "").trim().toLowerCase();
    const filtered = q
        ? users.filter(
            (u) =>
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q),
        )
        : users;
    if (!users.length) {
        return (
            <p
                data-testid="admin-users-empty"
                className="mt-6 rounded-2xl border border-dashed border-[#2e3220]/15 bg-white/60 px-4 py-8 text-center text-sm text-[#5a6142]"
            >
                No users yet.
            </p>
        );
    }
    if (!filtered.length) {
        return (
            <p
                data-testid="admin-users-no-match"
                className="mt-6 rounded-2xl border border-dashed border-[#2e3220]/15 bg-white/60 px-4 py-8 text-center text-sm text-[#5a6142]"
            >
                No users match "{filter}".
            </p>
        );
    }
    return (
        <ul
            data-testid="admin-users-list"
            className="mt-5 divide-y divide-[#2e3220]/10 overflow-hidden rounded-2xl border border-[#2e3220]/10 bg-white"
        >
            {filtered.map((u) => (
                <li
                    key={u.user_id}
                    data-testid={`admin-user-row-${u.user_id}`}
                    className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <p className="truncate font-semibold text-[#2e3220]">
                                {u.name || "(no name)"}
                            </p>
                            {u.profile_complete ? null : (
                                <span className="rounded-full bg-[#B56C5A]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#B56C5A]">
                                    incomplete
                                </span>
                            )}
                        </div>
                        <a
                            href={`mailto:${u.email}`}
                            className="block truncate text-xs text-[#5a6142] hover:text-[#737a3d]"
                        >
                            {u.email}
                        </a>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[#5a6142] sm:justify-end">
                        {u.zip_code ? (
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3" strokeWidth={2.5} />
                                {u.zip_code}
                            </span>
                        ) : null}
                        <span>{formatJoinDate(u.created_at)}</span>
                    </div>
                </li>
            ))}
        </ul>
    );
};

const StatCard = ({ testId, icon: Icon, label, value, loading, accent, onClick, expanded }) => {
    const bg = accent ? "bg-[#737a3d]" : "bg-[#F4F1EA]";
    const labelColor = accent ? "text-[#F4F1EA]/85" : "text-[#737a3d]";
    const valueColor = accent ? "text-[#F4F1EA]" : "text-[#2e3220]";
    const iconColor = accent ? "text-[#F4F1EA]/90" : "text-[#737a3d]";
    const border = accent ? "border-[#737a3d]" : "border-[#2e3220]/10";
    const clickable = typeof onClick === "function";
    const interactiveClasses = clickable
        ? "cursor-pointer text-left transition-transform hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#737a3d] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4F1EA]"
        : "";
    const inner = (
        <>
            <div className={`flex items-center justify-between ${iconColor}`}>
                <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" strokeWidth={2.25} />
                    <span
                        className={`text-[10px] font-bold uppercase tracking-widest ${labelColor}`}
                    >
                        {label}
                    </span>
                </div>
                {clickable ? (
                    expanded ? (
                        <ChevronUp className="h-4 w-4 opacity-70" strokeWidth={2.25} />
                    ) : (
                        <ChevronDown className="h-4 w-4 opacity-70" strokeWidth={2.25} />
                    )
                ) : null}
            </div>
            <div
                className={`mt-2 font-display text-3xl font-bold sm:text-4xl ${valueColor}`}
                data-testid={`${testId}-value`}
            >
                {loading ? (
                    <Loader2
                        className={`h-6 w-6 animate-spin ${accent ? "text-[#F4F1EA]" : "text-[#737a3d]"}`}
                        strokeWidth={2.5}
                    />
                ) : typeof value === "number" ? (
                    value.toLocaleString()
                ) : (
                    "—"
                )}
            </div>
        </>
    );
    const commonClass = `rounded-3xl border ${border} ${bg} p-4 sm:p-5 ${interactiveClasses}`;
    if (clickable) {
        return (
            <button
                type="button"
                data-testid={testId}
                onClick={onClick}
                aria-expanded={!!expanded}
                className={`${commonClass} block w-full`}
            >
                {inner}
            </button>
        );
    }
    return (
        <div data-testid={testId} className={commonClass}>
            {inner}
        </div>
    );
};

const Admin = () => {
    const [checking, setChecking] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [q, setQ] = useState("");
    const [owner, setOwner] = useState("");
    const [items, setItems] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetResult, setResetResult] = useState(null);
    const [resetting, setResetting] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    // Danger zone — nuclear marketplace reset (launch day tool). Guarded
    // by a "RESET" confirmation string on the server too.
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetConfirm, setResetConfirm] = useState("");
    const [resetting_marketplace, setResettingMarketplace] = useState(false);
    const [resetSummary, setResetSummary] = useState(null);
    // Top-line marketplace snapshot for the admin. Refreshes when the admin
    // page mounts (once per visit) — cheap set of count_documents calls.
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    // Users drawer — lazy-loaded the first time the admin taps the
    // "Total users" card, then just toggles visibility on subsequent taps.
    const [usersOpen, setUsersOpen] = useState(false);
    const [users, setUsers] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userFilter, setUserFilter] = useState("");

    const runMarketplaceReset = async () => {
        if (resetConfirm.trim() !== "RESET") {
            toast.error("Type RESET to confirm");
            return;
        }
        try {
            setResettingMarketplace(true);
            const res = await adminResetMarketplace(resetConfirm.trim());
            setResetSummary(res.wiped || {});
            toast.success("Marketplace reset complete", {
                description: `Deleted ${res.wiped?.users_deleted || 0} non-admin users, ${res.wiped?.items || 0} items, ${res.wiped?.rentals || 0} rentals.`,
                duration: 12000,
            });
            setResetDialogOpen(false);
            setResetConfirm("");
        } catch (err) {
            toast.error("Reset failed", {
                description: err?.response?.data?.detail || "Try again in a moment.",
            });
        } finally {
            setResettingMarketplace(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                await fetchAdminMe();
                setIsAdmin(true);
                // Fire-and-forget stats fetch — non-blocking so the rest of
                // the console still renders if this ever errors.
                setStatsLoading(true);
                try {
                    const s = await fetchAdminStats();
                    setStats(s);
                } catch (_) {
                    // silent — the stats row simply won't render
                } finally {
                    setStatsLoading(false);
                }
            } catch (e) {
                setIsAdmin(false);
            } finally {
                setChecking(false);
            }
        })();
    }, []);

    const toggleUsers = async () => {
        // If closing, just collapse — keep the list in memory so re-opening
        // is instant. If opening and we haven't loaded yet, fetch now.
        if (usersOpen) {
            setUsersOpen(false);
            return;
        }
        setUsersOpen(true);
        if (users === null) {
            try {
                setUsersLoading(true);
                const res = await fetchAdminUsers();
                setUsers(res.users || []);
            } catch (err) {
                toast.error("Couldn't load users", {
                    description: err?.response?.data?.detail || "Try again in a moment.",
                });
                setUsersOpen(false);
            } finally {
                setUsersLoading(false);
            }
        }
    };

    const doSearch = async (e) => {
        e?.preventDefault?.();
        if (!q.trim() && !owner.trim()) {
            toast.error("Enter an item query or owner filter");
            return;
        }
        try {
            setSearching(true);
            const data = await adminSearchItems({ q: q.trim(), owner: owner.trim() });
            setItems(data.items || []);
            setSearched(true);
        } catch (err) {
            toast.error("Search failed");
        } finally {
            setSearching(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            const res = await adminDeleteItem(deleteTarget.id);
            toast.success(
                `Deleted "${res.name || deleteTarget.name}" · ${res.rentals_flagged || 0} rentals preserved`,
            );
            setItems((prev) => prev.filter((i) => i.id !== deleteTarget.id));
            setDeleteTarget(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    const generateResetLink = async (e) => {
        e?.preventDefault?.();
        if (!resetEmail.trim()) {
            toast.error("Enter an email");
            return;
        }
        try {
            setResetting(true);
            setResetResult(null);
            const res = await adminGenerateResetLink(resetEmail.trim());
            setResetResult(res);
            toast.success(`Reset link ready for ${res.email}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't generate link");
        } finally {
            setResetting(false);
        }
    };

    const copyResetLink = async () => {
        if (!resetResult?.reset_link) return;
        try {
            await navigator.clipboard.writeText(resetResult.reset_link);
            setCopiedLink(true);
            toast.success("Reset link copied");
            setTimeout(() => setCopiedLink(false), 2000);
        } catch {
            toast.error("Couldn't copy — long-press to select and copy");
        }
    };

    if (checking) {
        return (
            <section className="mx-auto max-w-4xl px-6 py-24 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#737a3d]" />
                <p className="mt-3 text-sm text-[#5a6142]">Checking access…</p>
            </section>
        );
    }

    if (!isAdmin) {
        return (
            <section
                data-testid="admin-forbidden"
                className="mx-auto max-w-2xl px-6 py-24 text-center"
            >
                <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#B56C5A]/10 text-[#B56C5A]">
                    <ShieldCheck className="h-7 w-7" strokeWidth={2.25} />
                </div>
                <h1 className="font-display text-3xl font-bold text-[#2e3220]">
                    Not authorized
                </h1>
                <p className="mt-2 text-sm text-[#5a6142]">
                    This page is restricted to Lendly admins.
                </p>
            </section>
        );
    }

    return (
        <section
            data-testid="admin-page"
            className="relative z-10 mx-auto max-w-6xl px-6 py-14 md:px-10 md:py-20"
        >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <ShieldCheck
                    className="h-3.5 w-3.5 text-[#737a3d]"
                    strokeWidth={2.5}
                />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    Admin console
                </span>
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                Item cleanup
            </h1>
            <p className="mt-2 max-w-xl text-base leading-relaxed text-[#5a6142]">
                Search listings by name or owner, then remove stuck items that a
                user can no longer reach from their own dashboard. Rentals tied
                to a deleted item are preserved for analytics.
            </p>

            {/* Top-line marketplace snapshot */}
            <div
                data-testid="admin-stats-row"
                className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4"
            >
                <StatCard
                    testId="admin-stat-users-total"
                    icon={Users}
                    label="Total users"
                    value={stats?.users_total}
                    loading={statsLoading}
                    onClick={toggleUsers}
                    expanded={usersOpen}
                />
                <StatCard
                    testId="admin-stat-users-new"
                    icon={UserPlus}
                    label="New this week"
                    value={stats?.users_new_7d}
                    loading={statsLoading}
                    accent
                />
                <StatCard
                    testId="admin-stat-items-total"
                    icon={Package}
                    label="Items listed"
                    value={stats?.items_total}
                    loading={statsLoading}
                />
                <StatCard
                    testId="admin-stat-rentals-active"
                    icon={Repeat}
                    label="Active rentals"
                    value={stats?.rentals_active}
                    loading={statsLoading}
                />
            </div>

            {usersOpen ? (
                <div
                    data-testid="admin-users-panel"
                    className="mt-4 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-5"
                >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="font-display text-xl font-bold text-[#2e3220]">
                                Registered users
                            </h2>
                            <p className="text-xs text-[#5a6142]">
                                Newest first · {users ? `${users.length} total` : "loading…"}
                            </p>
                        </div>
                        <label className="block sm:w-72">
                            <span className="sr-only">Filter users</span>
                            <input
                                data-testid="admin-users-filter"
                                type="text"
                                value={userFilter}
                                onChange={(e) => setUserFilter(e.target.value)}
                                placeholder="Filter by name or email…"
                                className="w-full rounded-2xl border border-[#2e3220]/15 bg-white px-4 py-2 text-sm text-[#2e3220] outline-none focus:border-[#737a3d]"
                            />
                        </label>
                    </div>

                    {usersLoading ? (
                        <div
                            data-testid="admin-users-loading"
                            className="mt-6 flex items-center justify-center py-8 text-sm text-[#5a6142]"
                        >
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={2.5} />
                            Loading users…
                        </div>
                    ) : (
                        <UsersList users={users || []} filter={userFilter} />
                    )}
                </div>
            ) : null}

            <form
                onSubmit={doSearch}
                data-testid="admin-search-form"
                className="mt-8 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-5"
            >
                <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                    <label className="block">
                        <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Item (name or description)
                        </span>
                        <input
                            data-testid="admin-search-q"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="drill, kayak, ladder…"
                            className="w-full rounded-2xl border border-[#2e3220]/15 bg-white px-4 py-2.5 text-sm text-[#2e3220] outline-none focus:border-[#737a3d]"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Owner (name or email)
                        </span>
                        <input
                            data-testid="admin-search-owner"
                            type="text"
                            value={owner}
                            onChange={(e) => setOwner(e.target.value)}
                            placeholder="vondrehle, stephanie@…"
                            className="w-full rounded-2xl border border-[#2e3220]/15 bg-white px-4 py-2.5 text-sm text-[#2e3220] outline-none focus:border-[#737a3d]"
                        />
                    </label>
                    <button
                        data-testid="admin-search-submit"
                        type="submit"
                        disabled={searching}
                        className="inline-flex items-center justify-center gap-2 self-end rounded-2xl bg-[#737a3d] px-5 py-2.5 text-sm font-bold text-[#F4F1EA] transition-colors hover:bg-[#5a6142] disabled:opacity-60"
                    >
                        {searching ? (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
                        ) : (
                            <Search className="h-4 w-4" strokeWidth={2.5} />
                        )}
                        {searching ? "Searching…" : "Search"}
                    </button>
                </div>
            </form>

            {/* Password reset generator — for when Resend can't deliver
                a forgot-password email (missing env var, spam filters, etc.).
                Admin pastes the target user's email; backend returns a
                fresh reset link the admin can hand off. */}
            <div className="mt-8 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-5">
                <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                    <KeyRound className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Password reset link
                </div>
                <form
                    onSubmit={generateResetLink}
                    data-testid="admin-reset-form"
                    className="grid gap-3 md:grid-cols-[1fr_auto]"
                >
                    <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        data-testid="admin-reset-email"
                        placeholder="user@example.com"
                        className="w-full rounded-2xl border border-[#2e3220]/15 bg-white px-4 py-2.5 text-sm text-[#2e3220] outline-none focus:border-[#737a3d]"
                    />
                    <button
                        data-testid="admin-reset-submit"
                        type="submit"
                        disabled={resetting}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#737a3d] bg-transparent px-5 py-2.5 text-sm font-bold text-[#737a3d] transition-colors hover:bg-[#737a3d]/10 disabled:opacity-60"
                    >
                        {resetting ? (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
                        ) : (
                            <KeyRound className="h-4 w-4" strokeWidth={2.5} />
                        )}
                        {resetting ? "Generating…" : "Generate link"}
                    </button>
                </form>
                {resetResult && (
                    <div
                        data-testid="admin-reset-result"
                        className="mt-3 rounded-2xl border border-[#737a3d]/30 bg-white p-4 text-sm"
                    >
                        <p className="text-xs text-[#5a6142]">
                            One-time link for{" "}
                            <strong>{resetResult.email}</strong> — expires at{" "}
                            {new Date(resetResult.expires_at).toLocaleString()}
                        </p>
                        <div className="mt-2 flex items-start gap-2">
                            <code
                                data-testid="admin-reset-link"
                                className="min-w-0 flex-1 break-all rounded-xl bg-[#F4F1EA] px-3 py-2 font-mono text-[11px] text-[#2e3220]"
                            >
                                {resetResult.reset_link}
                            </code>
                            <button
                                type="button"
                                data-testid="admin-reset-copy"
                                onClick={copyResetLink}
                                className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-[#737a3d]/40 bg-transparent px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d] transition-colors hover:bg-[#737a3d]/10"
                            >
                                {copiedLink ? (
                                    <CheckIcon className="h-3 w-3" strokeWidth={2.5} />
                                ) : (
                                    <Copy className="h-3 w-3" strokeWidth={2.5} />
                                )}
                                {copiedLink ? "Copied" : "Copy"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-8" data-testid="admin-results">
                {!searched ? (
                    <p className="rounded-3xl border border-dashed border-[#2e3220]/15 p-10 text-center text-sm text-[#5a6142]">
                        Enter a query above to search listings across all users.
                    </p>
                ) : items.length === 0 ? (
                    <p
                        data-testid="admin-results-empty"
                        className="rounded-3xl border border-dashed border-[#2e3220]/15 p-10 text-center text-sm text-[#5a6142]"
                    >
                        No listings matched your search.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {items.map((it) => (
                            <li
                                key={it.id}
                                data-testid={`admin-item-row-${it.id}`}
                                className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] p-4"
                            >
                                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl border border-[#2e3220]/10 bg-[#E8E2D0]">
                                    {it.image_url && (
                                        <img
                                            src={it.image_url}
                                            alt={it.name}
                                            className="h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <p className="font-display text-base font-bold text-[#2e3220]">
                                            {it.name || "(untitled)"}
                                        </p>
                                        {it.status && (
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_CLASS[it.status] || STATUS_CLASS.inactive}`}
                                            >
                                                {it.status}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#5a6142]">
                                        <span className="inline-flex items-center gap-1">
                                            <UserIcon className="h-3 w-3" />
                                            {it.owner_name || "(unknown)"}
                                        </span>
                                        {it.owner_email && (
                                            <span className="inline-flex items-center gap-1">
                                                <Mail className="h-3 w-3" />
                                                {it.owner_email}
                                            </span>
                                        )}
                                        {(it.park || it.location) && (
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                {it.park || it.location}
                                            </span>
                                        )}
                                    </p>
                                    <p className="mt-1 font-mono text-[10px] text-[#5a6142]/70">
                                        {it.id}
                                    </p>
                                </div>
                                <button
                                    data-testid={`admin-delete-btn-${it.id}`}
                                    onClick={() => setDeleteTarget(it)}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-[#B56C5A]/40 px-4 py-2 text-xs font-bold text-[#B56C5A] transition-colors hover:bg-[#B56C5A]/10"
                                >
                                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                    Delete
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Danger zone — launch-day marketplace wipe */}
            <div
                data-testid="admin-danger-zone"
                className="mt-10 rounded-3xl border-2 border-[#B56C5A]/40 bg-[#F4F1EA] p-6"
            >
                <div className="flex items-start gap-3">
                    <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[#B56C5A] text-[#F4F1EA]">
                        <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-display text-xl font-bold text-[#2e3220]">
                            Danger zone · Reset marketplace
                        </h2>
                        <p className="mt-1 text-sm leading-relaxed text-[#5a6142]">
                            One-tap launch reset. Wipes every item, rental, review, question,
                            notification, payment transaction, withdrawal, and push subscription,
                            plus all non-admin user accounts. Admin accounts remain and their
                            credit balance is zeroed. <strong>This cannot be undone.</strong>
                        </p>
                        {resetSummary && (
                            <div className="mt-4 rounded-2xl border border-[#2e3220]/10 bg-white/60 p-4">
                                <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                    Last reset — summary
                                </p>
                                <pre className="mt-2 max-h-40 overflow-auto text-[11px] text-[#2e3220]">
                                    {JSON.stringify(resetSummary, null, 2)}
                                </pre>
                            </div>
                        )}
                        <button
                            type="button"
                            data-testid="admin-reset-marketplace-open"
                            onClick={() => setResetDialogOpen(true)}
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#B56C5A] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] transition-colors hover:bg-[#a55c4a]"
                        >
                            <Trash2 className="h-4 w-4" strokeWidth={2.5} />
                            Reset marketplace
                        </button>
                    </div>
                </div>
            </div>

            <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                <AlertDialogContent
                    data-testid="admin-reset-marketplace-dialog"
                    className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220]"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl font-bold text-[#B56C5A]">
                            Reset the entire marketplace?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-sm leading-relaxed text-[#5a6142]">
                            This will permanently delete every item, rental, review, question,
                            notification, payment transaction, withdrawal, and push subscription,
                            and remove every user account except admins. Type{" "}
                            <strong className="font-bold text-[#B56C5A]">RESET</strong> below to
                            confirm.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <input
                        type="text"
                        data-testid="admin-reset-marketplace-input"
                        value={resetConfirm}
                        onChange={(e) => setResetConfirm(e.target.value)}
                        autoFocus
                        placeholder="Type RESET to confirm"
                        className="mt-2 w-full rounded-2xl border border-[#B56C5A]/40 bg-white/70 px-4 py-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#B56C5A]"
                    />
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="admin-reset-marketplace-cancel"
                            onClick={() => {
                                setResetConfirm("");
                                setResetDialogOpen(false);
                            }}
                            disabled={resetting_marketplace}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="admin-reset-marketplace-confirm"
                            onClick={(e) => {
                                e.preventDefault();
                                runMarketplaceReset();
                            }}
                            disabled={resetting_marketplace || resetConfirm.trim() !== "RESET"}
                            className="bg-[#B56C5A] text-[#F4F1EA] hover:bg-[#a55c4a] disabled:opacity-50"
                        >
                            {resetting_marketplace ? "Resetting…" : "Reset marketplace"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>


            <AlertDialog
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
            >
                <AlertDialogContent data-testid="admin-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this listing?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteTarget && (
                                <>
                                    Permanently remove <strong>{deleteTarget.name}</strong>{" "}
                                    owned by <strong>{deleteTarget.owner_name || "unknown"}</strong>.
                                    Historical rentals are preserved for analytics
                                    (marked <code>item_deleted=true</code>). This can’t be undone.
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="admin-delete-cancel"
                            disabled={deleting}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="admin-delete-confirm"
                            onClick={(e) => {
                                e.preventDefault();
                                confirmDelete();
                            }}
                            disabled={deleting}
                            className="bg-[#B56C5A] text-[#F4F1EA] hover:bg-[#a55c4a]"
                        >
                            {deleting ? "Deleting…" : "Delete listing"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
};

export default Admin;
